import React, { useEffect, useState } from 'react';
import { Modal, Button, Row, Col, Alert } from 'react-bootstrap';
import { useQuestionnaireTemplates } from '../../hooks/useQuestionnaireTemplates';
import type {
  UserOrganisation,
  PaginationAndSorting,
  QuestionnaireTemplate,
} from '../../types/types';
import { LoaderContainer } from '../common/LoaderContainer';
import { useGetSuppliers } from '../../hooks/useGetSuppliers';
import { useSelector } from 'react-redux';
import { SupplierSelect } from '../common/SupplierSelect';
import { useSendAssessments } from '../../hooks/useSendAssessments';
import { tracking } from '../../util/tracking';

interface SendAssessmentToSupplierProps {
  templateId: string;
  show: boolean;
  onClose: () => void;
}

export const SendAssessmentToSupplier: React.FC<
  SendAssessmentToSupplierProps
> = ({ templateId, show, onClose }) => {
  const [template, setTemplate] = useState<QuestionnaireTemplate>();
  const [selectedSuppliers, setSelectedSuppliers] = useState<string[]>([]);

  const { getTemplateByTemplateId, loading: templateLoading } =
    useQuestionnaireTemplates();
  const selectedOrganisation = useSelector(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (state: any) => state.organisation.selected
  ) as UserOrganisation | undefined;

  const { sendAssessments, sending, error, result, reset } =
    useSendAssessments();

  const defaultPaginationAndSorting: PaginationAndSorting = {
    page: 1,
    pageSize: 10,
    search: '',
    sortBy: 'updated_at',
    sortDirection: 'desc',
  };

  const { loading: suppliersLoading, error: suppliersError } = useGetSuppliers(
    selectedOrganisation?.organisation?.id ?? '',
    defaultPaginationAndSorting
  );

  useEffect(() => {
    if (!show) {
      setSelectedSuppliers([]);
      reset(); // clear any previous result/error when closing modal
    }
  }, [show, reset]);

  useEffect(() => {
    const loadTemplate = async () => {
      const assessmentTemplate = (await getTemplateByTemplateId(
        templateId
      )) as QuestionnaireTemplate;
      setTemplate(assessmentTemplate);
    };
    loadTemplate();
  }, [templateId]);

  const handleSend = async () => {
    tracking.log('UI', 'assessment_send', {
      templateId,
      suppliers: selectedSuppliers.length,
    });
    await sendAssessments(templateId, selectedSuppliers);
  };

  const handleReset = () => {
    reset(); // reset error/result state in the hook
    setSelectedSuppliers([]); // reset supplier selection
  };

  return (
    <Modal show={show} onHide={onClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{template?.title}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {templateLoading || suppliersLoading || sending ? (
          <LoaderContainer />
        ) : (
          <Row>
            {result?.success ? (
              <Col className="text-center">
                <Alert variant="success">
                  <h5>✅ Assessments Sent Successfully!</h5>
                </Alert>
                <Button variant="primary" onClick={handleReset}>
                  Send More
                </Button>
              </Col>
            ) : error ? (
              <Col className="text-center">
                <Alert variant="danger">
                  <h5>❌ Failed to Send Assessments</h5>
                  <p>{error}</p>
                </Alert>
                <Button variant="primary" onClick={handleReset}>
                  Try Again
                </Button>
              </Col>
            ) : (
              <Col>
                Select suppliers to send the questionnaire to:
                <div className="text-muted mt-2">
                  {suppliersError ? (
                    <p className="text-center text-danger">
                      There was an error getting suppliers.
                    </p>
                  ) : (
                    <SupplierSelect
                      onSelectionChange={(selected) => {
                        setSelectedSuppliers(selected);
                      }}
                    />
                  )}
                </div>
              </Col>
            )}
          </Row>
        )}
      </Modal.Body>

      <Modal.Footer className="d-flex">
        <p className="flex-fill mb-0">
          {selectedSuppliers?.length < 1 ? (
            <>Select suppliers</>
          ) : (
            <span>Selected: {selectedSuppliers.length}</span>
          )}
        </p>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
        {!result?.success && (
          <Button
            variant="primary"
            disabled={!selectedSuppliers.length || sending}
            onClick={handleSend}
          >
            {sending ? 'Sending...' : 'Send'}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};
